import React, { useState, useEffect } from 'react';
import { doc, getDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../context/AuthContext';
import { getFunctions, httpsCallable } from 'firebase/functions';
import './SubscriptionPlan.css';

function SubscriptionPlan() {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showChangeModal, setShowChangeModal] = useState(false);
  const [pendingPlan, setPendingPlan] = useState(null);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [activeScreenCount, setActiveScreenCount] = useState(0);
  
  // Subscription data
  const [subscription, setSubscription] = useState({
    plan: '1-screen',
    screensAllowed: 1,
    screensUsed: 0,
    pricePerMonth: 30,
    discountCode: null,
    discountPercent: 0,
    status: 'active',
    nextBillingDate: null,
    cancelAt: null
  });

  const plans = {
    '1-screen': {
      name: 'Standard',
      screens: 1,
      price: 30,
      priceId: process.env.REACT_APP_PRICE_STANDARD,
      features: ['Multi-layout (5-6 zones)', 'Synced playback', 'Full video library', 'Equipment filtering', 'Content scheduling', 'Request exercises']
    },
    '2-screen': {
      name: 'Standard Plus',
      screens: 2,
      price: 60,
      priceId: process.env.REACT_APP_PRICE_STANDARD_PLUS,
      features: ['Multi-layout per screen', 'Synced playback', 'Full video library', 'Equipment filtering', 'Content scheduling', 'Request exercises']
    },
    '5-screen': {
      name: 'Pro',
      screens: 5,
      price: 99,
      priceId: process.env.REACT_APP_PRICE_PRO,
      features: ['Advanced custom layouts', 'Synced playback', 'Full video library', 'Equipment filtering', 'Content scheduling', 'Priority requests']
    },
    '15-screen': {
      name: 'Enterprise',
      screens: 15,
      price: 149,
      priceId: process.env.REACT_APP_PRICE_ENTERPRISE,
      features: ['Advanced custom layouts', 'Multi-location support', 'Synced playback', 'Full video library', 'Equipment filtering', 'Priority requests']
    }
  };

  const taxRate = 0.0975; // Tennessee 9.75%

  useEffect(() => {
  const fetchSubscription = async () => {
    if (currentUser?.uid) {
      try {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          
          if (data.subscription) {
            setSubscription(prev => ({
              ...prev,
              ...data.subscription,
              discountCode: data.subscription.discountCode || null,
              discountPercent: data.subscription.discountPercent || 0
            }));
          }
        }

// Count active screens from screens collection
const userData = userDoc.exists() ? userDoc.data() : {};
const isAdmin = userData.role === 'super_admin' || userData.role === 'admin';

const screensQuery = isAdmin 
  ? collection(db, 'screens')
  : query(collection(db, 'screens'), where('userId', '==', currentUser.uid));

const screensSnapshot = await getDocs(screensQuery);

const userScreens = screensSnapshot.docs
  .map(doc => doc.data())
  .filter(screen => {
    if (isAdmin) {
      return !screen.userId && screen.screenStatus !== 'inactive';
    } else {
      return screen.screenStatus !== 'inactive';
    }
  });
setActiveScreenCount(userScreens.length);

      } catch (error) {
        console.error('Error fetching subscription:', error);
      }
    }
    setLoading(false);
  };
  fetchSubscription();
}, [currentUser]);

  const currentPlan = plans[subscription.plan] || plans['1-screen'];
  const basePrice = currentPlan.price;
  
  const discountPercent = subscription.discountPercent || 0;
  const discountAmount = discountPercent > 0 ? basePrice * (discountPercent / 100) : 0;
  const subtotalAfterDiscount = basePrice - discountAmount;
  const taxAmount = subtotalAfterDiscount * taxRate;
  const totalWithTax = subtotalAfterDiscount + taxAmount;

  const formatDate = (date) => {
    if (!date) {
      const nextMonth = new Date();
      nextMonth.setMonth(nextMonth.getMonth() + 1);
      return nextMonth.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    }
    return new Date(date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const handlePlanChangeClick = (planKey) => {
  if (planKey === subscription.plan) return;
  
  const newPlan = plans[planKey];
  const currentPlanScreens = plans[subscription.plan]?.screens || 1;
  
  // Check if this is a downgrade
  if (newPlan.screens < currentPlanScreens) {
    // Check if user has too many active screens for the new plan
    if (activeScreenCount > newPlan.screens) {
      const screensToMove = activeScreenCount - newPlan.screens;
      alert(`You have ${activeScreenCount} active screen${activeScreenCount > 1 ? 's' : ''}. Move ${screensToMove} screen${screensToMove > 1 ? 's' : ''} to Inactive before downgrading to ${newPlan.name}.`);
      return;
    }
  }
  
  setPendingPlan(planKey);
  setShowChangeModal(true);
};

  const confirmPlanChange = async () => {
    if (!pendingPlan) return;
    
    setProcessing(true);
    setError(null);
    
    try {
      const functions = getFunctions();
      const updateSubscription = httpsCallable(functions, 'updateSubscription');
      
      const newPlan = plans[pendingPlan];
      const result = await updateSubscription({
        userId: currentUser.uid,
        newPriceId: newPlan.priceId
      });
      
      if (result.data.success) {
        setSubscription(prev => ({
          ...prev,
          plan: pendingPlan,
          screensAllowed: newPlan.screens,
          pricePerMonth: newPlan.price
        }));
        setSuccessMessage(`Successfully ${pendingPlan === '2-screen' ? 'upgraded' : 'downgraded'} to ${newPlan.name}`);
        setTimeout(() => setSuccessMessage(null), 5000);
      } else {
        setError(result.data.error || 'Failed to update subscription');
      }
    } catch (err) {
      console.error('Error updating subscription:', err);
      setError(err.message || 'Failed to update subscription');
    } finally {
      setProcessing(false);
      setShowChangeModal(false);
      setPendingPlan(null);
    }
  };

  const handleCancelSubscription = async () => {
    setProcessing(true);
    setError(null);
    
    try {
      const functions = getFunctions();
      const cancelSubscription = httpsCallable(functions, 'cancelSubscription');
      
      const result = await cancelSubscription({
        userId: currentUser.uid
      });
      
      if (result.data.success) {
        setSubscription(prev => ({
          ...prev,
          status: 'canceling',
          cancelAt: result.data.cancelAt
        }));
        setSuccessMessage('Subscription will cancel at end of billing period');
        setTimeout(() => setSuccessMessage(null), 5000);
      } else {
        setError(result.data.error || 'Failed to cancel subscription');
      }
    } catch (err) {
      console.error('Error canceling subscription:', err);
      setError(err.message || 'Failed to cancel subscription');
    } finally {
      setProcessing(false);
      setShowCancelModal(false);
    }
  };

  if (loading) {
    return (
      <div className="subscription-page">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="subscription-page">
      <div className="subscription-header">
        <h1>Subscription Plan</h1>
        <p>Manage your subscription and billing</p>
      </div>

      {/* Success/Error Messages */}
      {successMessage && (
        <div className="alert alert-success">{successMessage}</div>
      )}
      {error && (
        <div className="alert alert-error">{error}</div>
      )}

      {/* Current Plan Card */}
      <div className="subscription-card">
        {/* Screen Usage Visual */}
        <div className="screen-usage">
          <div className="screen-count">
            {activeScreenCount}
            <span className="separator">/</span>
            <span className="total">{subscription.screensAllowed}</span>
          </div>
          <div className="screen-label">
            {subscription.screensAllowed === 1 ? 'screen' : 'screens'}
          </div>
        </div>

        {/* Plan Details */}
        <div className="plan-details">
          <div className="plan-row">
            <span className="plan-row-label">Plan</span>
            <span className="plan-row-value">{currentPlan.name}</span>
          </div>
          
          <div className="plan-row">
            <span className="plan-row-label">Price</span>
            <span className="plan-row-value">
              ${basePrice.toFixed(2)}/month
              {currentPlan.screens > 1 && (
                <span className="per-screen"> (${(basePrice / currentPlan.screens).toFixed(2)}/screen)</span>
              )}
            </span>
          </div>

          {subscription.discountCode && discountPercent > 0 && (
            <div className="plan-row discount">
              <span className="plan-row-label">Discount ({subscription.discountCode})</span>
              <span className="plan-row-value">-{discountPercent}%</span>
            </div>
          )}
          
          <div className="plan-row">
            <span className="plan-row-label">Total</span>
            <span className="plan-row-value">${subtotalAfterDiscount.toFixed(2)}/month</span>
          </div>

          <div className="plan-row">
            <span className="plan-row-label">Upcoming charge</span>
            <span className="plan-row-value">
              ${totalWithTax.toFixed(2)}
              <span className="details-link" onClick={() => setShowDetailsModal(true)}>
                (Details)
              </span>
            </span>
          </div>
        </div>

        {/* Billing Info */}
        <div className="billing-info">
          <div>
            <div className="billing-date-label">Next billing date</div>
            <div className="billing-date-value">{formatDate(subscription.nextBillingDate)}</div>
          </div>
          <div className={`status-badge ${subscription.status !== 'active' ? 'inactive' : ''}`}>
            {subscription.status === 'active' ? 'Active' : 
             subscription.status === 'canceling' ? 'Canceling' : 'Inactive'}
          </div>
        </div>

        {/* Cancel Link - inside card */}
        {subscription.status === 'active' && (
          <div className="cancel-link-wrapper">
            <span 
              className="cancel-link"
              onClick={() => setShowCancelModal(true)}
            >
              Cancel subscription
            </span>
          </div>
        )}

        {/* Cancellation Notice */}
        {subscription.status === 'canceling' && subscription.cancelAt && (
          <div className="cancellation-notice">
            Your subscription will end on {formatDate(subscription.cancelAt)}
          </div>
        )}
      </div>

      {/* Plan Selection */}
      <div className="plan-cards-section">
        <h3>Change Plan</h3>
        
        <div className="plan-cards">
          {/* 1 Screen Plan */}
          <div className={`plan-card ${subscription.plan === '1-screen' ? 'selected' : ''}`}>
            <div className="plan-card-tier">Standard</div>
            <div className="plan-card-price">$30<span>/mo</span></div>
            <div className="plan-card-screens">1 Screen</div>
            <ul className="plan-card-features">
              {plans['1-screen'].features.map((feature, i) => (
                <li key={i}>{feature}</li>
              ))}
            </ul>
            {subscription.plan === '1-screen' ? (
              <button className="plan-card-btn current" disabled>Current Plan</button>
            ) : (
              <button 
                className="plan-card-btn downgrade"
                onClick={() => handlePlanChangeClick('1-screen')}
                disabled={processing || subscription.status === 'canceling'}
              >
                Downgrade
              </button>
            )}
          </div>

          {/* 2 Screen Plan */}
          <div className={`plan-card ${subscription.plan === '2-screen' ? 'selected' : ''}`}>
            <div className="plan-card-tier">Standard Plus</div>
            <div className="plan-card-price">$60<span>/mo</span></div>
            <div className="plan-card-screens">2 Screens</div>
            <ul className="plan-card-features">
              {plans['2-screen'].features.map((feature, i) => (
                <li key={i}>{feature}</li>
              ))}
            </ul>
            {subscription.plan === '2-screen' ? (
              <button className="plan-card-btn current" disabled>Current Plan</button>
            ) : (
              <button 
                className={`plan-card-btn ${plans['2-screen'].screens > (plans[subscription.plan]?.screens || 1) ? 'upgrade' : 'downgrade'}`}
                onClick={() => handlePlanChangeClick('2-screen')}
                disabled={processing || subscription.status === 'canceling'}
              >
                {plans['2-screen'].screens > (plans[subscription.plan]?.screens || 1) ? 'Upgrade' : 'Downgrade'}
              </button>
            )}
          </div>

          {/* 5 Screen Plan */}
          <div className={`plan-card ${subscription.plan === '5-screen' ? 'selected' : ''}`}>
            <div className="plan-card-tier">Pro</div>
            <div className="plan-card-price">$99<span>/mo</span></div>
            <div className="plan-card-screens">Up to 5 Screens</div>
            <ul className="plan-card-features">
              {plans['5-screen'].features.map((feature, i) => (
                <li key={i}>{feature}</li>
              ))}
            </ul>
            {subscription.plan === '5-screen' ? (
              <button className="plan-card-btn current" disabled>Current Plan</button>
            ) : (
              <button 
                className={`plan-card-btn ${plans['5-screen'].screens > (plans[subscription.plan]?.screens || 1) ? 'upgrade' : 'downgrade'}`}
                onClick={() => handlePlanChangeClick('5-screen')}
                disabled={processing || subscription.status === 'canceling'}
              >
                {plans['5-screen'].screens > (plans[subscription.plan]?.screens || 1) ? 'Upgrade' : 'Downgrade'}
              </button>
            )}
          </div>

          {/* 15 Screen Plan */}
          <div className={`plan-card ${subscription.plan === '15-screen' ? 'selected' : ''}`}>
            <div className="plan-card-tier">Enterprise</div>
            <div className="plan-card-price">$149<span>/mo</span></div>
            <div className="plan-card-screens">Up to 15 Screens</div>
            <ul className="plan-card-features">
              {plans['15-screen'].features.map((feature, i) => (
                <li key={i}>{feature}</li>
              ))}
            </ul>
            {subscription.plan === '15-screen' ? (
              <button className="plan-card-btn current" disabled>Current Plan</button>
            ) : (
              <button 
                className={`plan-card-btn ${plans['15-screen'].screens > (plans[subscription.plan]?.screens || 1) ? 'upgrade' : 'downgrade'}`}
                onClick={() => handlePlanChangeClick('15-screen')}
                disabled={processing || subscription.status === 'canceling'}
              >
                {plans['15-screen'].screens > (plans[subscription.plan]?.screens || 1) ? 'Upgrade' : 'Downgrade'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Plan Change Confirmation Modal */}
      {showChangeModal && pendingPlan && (
        <div className="modal-overlay" onClick={() => !processing && setShowChangeModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Confirm Plan Change</h2>
            <p>
              {plans[pendingPlan]?.screens > (plans[subscription.plan]?.screens || 1)
                ? `You're upgrading to ${plans[pendingPlan]?.name} ($${plans[pendingPlan]?.price}/mo). You'll be charged a prorated amount for the remainder of this billing cycle.`
                : `You're downgrading to ${plans[pendingPlan]?.name} ($${plans[pendingPlan]?.price}/mo). You'll receive a prorated credit for the remainder of this billing cycle.`
              }
            </p>
            <div className="modal-actions">
              <button 
                className="btn-secondary" 
                onClick={() => setShowChangeModal(false)}
                disabled={processing}
              >
                Cancel
              </button>
              <button 
                className={`btn-primary ${pendingPlan === '2-screen' ? 'upgrade' : 'downgrade'}`}
                onClick={confirmPlanChange}
                disabled={processing}
              >
                {processing ? 'Processing...' : `Confirm ${plans[pendingPlan]?.screens > (plans[subscription.plan]?.screens || 1) ? 'Upgrade' : 'Downgrade'}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Subscription Modal */}
      {showCancelModal && (
        <div className="modal-overlay" onClick={() => !processing && setShowCancelModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Cancel Subscription?</h2>
            <p>
              Your subscription will remain active until the end of your current billing period. 
              After that, you'll lose access to your screens and content.
            </p>
            <div className="modal-actions">
              <button 
                className="btn-secondary" 
                onClick={() => setShowCancelModal(false)}
                disabled={processing}
              >
                Keep Subscription
              </button>
              <button 
                className="btn-danger"
                onClick={handleCancelSubscription}
                disabled={processing}
              >
                {processing ? 'Processing...' : 'Yes, Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && (
        <div className="modal-overlay" onClick={() => setShowDetailsModal(false)}>
          <div className="details-modal" onClick={(e) => e.stopPropagation()}>
            <div className="details-modal-header">
              <h2>Upcoming Charge</h2>
            </div>

            <div className="details-modal-body">
              <div className="details-table-header">
                <div>Description</div>
                <div style={{ textAlign: 'center' }}>Qty</div>
                <div style={{ textAlign: 'right' }}>Amount</div>
              </div>

              <div className="details-line-item">
                <div>1 × {currentPlan.name} (at ${basePrice.toFixed(2)}/month)</div>
                <div className="qty">1</div>
                <div className="amount">${basePrice.toFixed(2)}</div>
              </div>

              <div className="details-row">
                <div className="label">Subtotal</div>
                <div className="value">${basePrice.toFixed(2)}</div>
              </div>

              {subscription.discountCode && discountPercent > 0 && (
                <div className="details-row discount">
                  <div className="label">Discount ({subscription.discountCode})</div>
                  <div className="value">-${discountAmount.toFixed(2)}</div>
                </div>
              )}

              <div className="details-row">
                <div className="label">Sales Tax - Tennessee (9.75%)</div>
                <div className="value">${taxAmount.toFixed(2)}</div>
              </div>

              <div className="details-row total">
                <div className="label">Total</div>
                <div className="value">${totalWithTax.toFixed(2)}</div>
              </div>

              <div className="details-row">
                <div className="label">Amount due</div>
                <div className="value" style={{ fontWeight: '600' }}>${totalWithTax.toFixed(2)}</div>
              </div>
            </div>

            <div className="details-modal-footer">
              <button className="close-btn" onClick={() => setShowDetailsModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SubscriptionPlan;